import fetchJsonp from 'fetch-jsonp';
import lru from 'lru-cache';

import {getRecentSearchesSorted} from './functions/recent';
import {highlightResults, isValidPropertyId} from './functions/searchinput';
import {
    mapModel,
    mapGoogleModel,
    mapListingModel
} from './functions/modelMapper';
import {VRBO_LANDING_EGUAS_TYPEAHEAD} from './experiments';
import {filterEligbleGoogleResults} from './functions/googleResultsUtils';
import {isListingIdSearchTurnedOn} from './functions/destinationHelper';

const cache = lru(100); // maximum of 100 entries into cache

function getRandomNumber() {
    return Math.floor(Math.random() * 10000000000);
}
export class TypeaheadStore {
    config = {};

    isLoggedIn;
    isHistoryFromEGUAS;

    /**
     * init()
     * Initialize the store
     */
    constructor(config) {
        this.config = config;
        this.isLoggedIn =
            window.analyticsdatalayer &&
            window.analyticsdatalayer.publicuuid !== '-1';
        this.isHistoryFromEGUAS =
            window.__ABACUS__ &&
            window.__ABACUS__[VRBO_LANDING_EGUAS_TYPEAHEAD] &&
            window.__ABACUS__[VRBO_LANDING_EGUAS_TYPEAHEAD].value > 0;
    }

    /**
     * getConfig()
     * Get configuration
     * @param {*} path
     */
    getConfig(path) {
        return this.config.getMergedState(path);
    }

    /**
     * writeToCache()
     * Interface between this class and writing to cache structure
     * @param {*} key
     * @param {*} data
     */
    writeToCache(key, data) {
        return cache.set(key.toLowerCase(), data);
    }

    /**
     * readFromCache()
     * Interface between this class and reading from cache structure
     * @param {*} key
     */
    readFromCache(key) {
        return cache.get(key.toLowerCase());
    }

    /**
     * convertToUIModel
     * Converts supplied data to uniform UI model that consuming apps depend on via `onUpdate()` interface
     * Removal or Changes to existing top level data properties should be MAJOR breaking changes
     * Additions to top level data properties should be MINOR / PATCH changes
     * @param {*} suggestions
     * @param apiResultsFrom
     * @param searchInput
     * @param resultsUuid
     */
    // eslint-disable-next-line complexity
    convertToUIModel(suggestions, apiResultsFrom, searchInput, resultsUuid) {
        suggestions = suggestions.map((suggestion) => {
            if (apiResultsFrom === 'GOOGLE') {
                return mapGoogleModel(suggestion);
            }
            if (
                isListingIdSearchTurnedOn() &&
                suggestion.listingProps &&
                Object.keys(suggestion.listingProps).length
            ) {
                return mapListingModel(suggestion);
            }
            return mapModel(suggestion);
        });

        // Get Recent searches
        const recents =
            this.isLoggedIn && this.isHistoryFromEGUAS
                ? []
                : getRecentSearchesSorted(
                      this.getConfig('locale'),
                      searchInput,
                      this.getConfig('recentSearchesDisplayMax')
                  );
        const matchMode = this.getConfig('matchMode');
        let results =
            this.isLoggedIn && this.isHistoryFromEGUAS
                ? suggestions
                : recents.concat(suggestions);

        // Process Highlighting
        let resultsWithHighlights = highlightResults(
            searchInput,
            results,
            matchMode
        );

        return {
            results,
            resultsWithHighlights,
            poweredByGoogle: apiResultsFrom === 'GOOGLE',
            resultsUuid: resultsUuid || null
        };
    }

    /**
     * stringifyQuery()
     * Dependency free query param encoding.  Light-weight alternative to packages like `qs` and `query-string`.
     * Fetch does not support query param encoding as there is no universally accepted standard (yet)
     * See https://github.com/github/fetch/issues/256
     * @param {*} params
     */
    stringifyQuery(params) {
        let esc = encodeURIComponent;
        let query = Object.keys(params)
            .map((k) => `${esc(k)}=${esc(params[k])}`)
            .join('&');
        return query;
    }

    /**
     * convertLocaleFormat()
     * Convert locale to 'languageCode_countryCode' format
     */
    convertLocaleFormat(locale) {
        return locale.replace('-', '_').substr(0, 5);
    }

    /**
     * Helper function to ensure registered API routes have consistent slashes
     * @param {*} input
     */
    ensureStartsAndEndsWithSlash(input) {
        return input.replace(/^(\/)?([^\/]*)(\/)?$/, '/$2/').replace('//', '/');
    }

    /**
     * getGoogleSuggestions()
     * Get suggestion results from Google
     * @param {*} input
     */
    getGoogleSuggestions(input) {
        let prefix = this.ensureStartsAndEndsWithSlash(
            this.getConfig('appPrefix')
        );
        const googlePlaceSuggestionsUrl = `${prefix}getGooglePlaceSuggestions`;

        const language = this.getConfig('locale');

        let query = this.stringifyQuery({
            input,
            language
        });

        let requestUrl = `${googlePlaceSuggestionsUrl}?${query}`;

        return window
            .fetch(requestUrl)
            .then((response) => {
                return response.json();
            })
            .then((data) => {
                return data;
            });
    }

    /**
     * buildRemoteParams()
     * @param {*} searchInput
     */
    buildRemoteParams(searchInput) {
        let limit = this.getConfig('isMobile')
            ? this.getConfig('popoverMaxDisplayCountMobile')
            : this.getConfig('popoverMaxDisplayCount');
        return {
            site: this.getConfig('site'),
            size: limit,
            input: searchInput,
            locale: this.convertLocaleFormat(this.getConfig('locale')),
            _: getRandomNumber(),
            _restfully: true
        };
    }

    _fetchResults({url, searchInput, getResultsIndex, callback}) {
        return fetchJsonp(url)
            .then((response) => response.json())
            .then((jsonData) => {
                // If we have zero results and it's not a valid property id, fallback to Google)
                if (
                    jsonData.suggestions.length === 0 &&
                    !isValidPropertyId(searchInput)
                ) {
                    return this.getGoogleSuggestions(searchInput).then(
                        (googleJsonData) => {
                            let results = filterEligbleGoogleResults(
                                googleJsonData.predictions
                            );
                            // Convert google results to uniform data results
                            let suggestions = this.convertToUIModel(
                                results,
                                'GOOGLE',
                                searchInput
                            );
                            // Write to cache
                            this.writeToCache(searchInput, suggestions);
                            // If this is latest request, invoke callback
                            if (getResultsIndex === this._getResultsIndex) {
                                callback(suggestions);
                            }
                            return suggestions;
                        }
                    );
                }
                // Convert results to uniform data results
                let suggestions = this.convertToUIModel(
                    jsonData.suggestions,
                    'HOMEAWAY',
                    searchInput,
                    jsonData.uuid
                );
                // Write To Cache
                this.writeToCache(searchInput, suggestions);
                // If this is latest request, invoke callback
                if (getResultsIndex === this._getResultsIndex) {
                    return callback(suggestions);
                }
                return suggestions;
            })
            .catch((ex) => {
                // eslint-disable-next-line no-console
                console.error(
                    'Exception occurred while requesting typeahead results:',
                    ex
                );
                delete this._pendingResultsHashMap[searchInput];
            });
    }

    /**
     * getResults()
     * Implements Bloodhound to optimize data retrival of search results either from cache or AJAX request
     * Uses local LRU Cache for data + highlight results
     * Uses "_getResultsIndex" and "_pendingResultsHashMap" to optimize rendering performance for last requested suggestisons.
     */
    _getResultsIndex = 0;
    _pendingResultsHashMap = {};
    getResults(searchInput, callback) {
        if (searchInput === false) {
            return this._getResultsIndex++;
        }
        // Truncate trailing spaces to optimize queries
        // IE Typing in ("Bora ") should re-use cached results from ("Bora")
        searchInput = searchInput.replace(/^(.*?)\s*$/, '$1');

        // Count unique resource request
        let getResultsIndex = ++this._getResultsIndex;

        let remoteParams = this.buildRemoteParams(searchInput);
        let remoteQueryString = `?${this.stringifyQuery(remoteParams)}`;
        let remotePath = this.getConfig('remotePath');

        const url = `${this.getConfig(
            'baseUrl'
        )}${remotePath}${remoteQueryString}`;
        // Check if we already have result in cache
        if (this.readFromCache(searchInput)) {
            return callback(this.readFromCache(searchInput));
        } else if (this._pendingResultsHashMap[searchInput]) {
            return this._pendingResultsHashMap[searchInput].then(
                (suggestions) => {
                    if (
                        suggestions &&
                        getResultsIndex === this._getResultsIndex
                    ) {
                        return callback(suggestions);
                    }
                    return false;
                }
            );
        }
        this._pendingResultsHashMap[searchInput] = this._fetchResults({
            url,
            searchInput,
            getResultsIndex,
            callback
        });
        return this._pendingResultsHashMap[searchInput];
    }

    _fetchTrending(url) {
        return fetchJsonp(url)
            .then((response) => response.json())
            .then((jsonData) => {
                let suggestions = this.convertToUIModel(
                    jsonData.suggestions,
                    'HOMEAWAY',
                    '',
                    jsonData.uuid
                );
                return suggestions;
            })
            .catch((ex) => {
                // eslint-disable-next-line no-console
                console.error(
                    'Exception occurred while requesting typeahead trending results:',
                    ex
                );
            });
    }

    _pendingTrendingResults = null;
    getTrending(callback) {
        if (!this._pendingTrendingResults) {
            let remotePath = this.getConfig('remotePath');
            const localeParam = `locale=${this.getConfig('locale')}`;
            const trendingPath = '/trending?';
            const url = `${this.getConfig(
                'baseUrl'
            )}${remotePath}${trendingPath}${localeParam}`;

            this._pendingTrendingResults = this._fetchTrending(url);
        }

        return this._pendingTrendingResults.then((suggestions) => {
            suggestions = suggestions || [];
            return callback(suggestions);
        });
    }
}
